/**
 * @fileoverview Low-cardinality client context attached to high-volume page events.
 * @description Helps explain GA4 "(not set)" OS/browser buckets — especially Consent
 * Mode cookieless pings, in-app WebViews, and Safari ITP storage gaps.
 *
 * @module lib/analytics/client-context
 */

import { ATTRIBUTION_COOKIE_NAME, ATTRIBUTION_STORAGE_KEY } from "./cookie-names"
import { parseAttributionCookie } from "./attribution-cookie"
import { analyticsConsentState, isConsentRequiredRegionClient } from "./consent-context"
import { CONSENT_CHANGED_EVENT } from "./consent"
import { detectInAppBrowser } from "./in-app-browser"

/** GA4-safe property bag for page-level diagnostics. */
export type ClientContextProps = Readonly<{
  viewport_bucket: string
  in_app_browser: boolean
  in_app_app: string
  region_consent_required: boolean
  analytics_consent_state: string
  storage_attribution_present: boolean
}>

let cached: ClientContextProps | null = null
let listenersBound = false

/**
 * Drops the cached client context (call after consent changes).
 *
 * @returns {void}
 */
export function invalidateClientContextCache(): void {
  cached = null
}

function readAttributionCookieRaw(): string | undefined {
  if (typeof document === "undefined") return undefined
  try {
    const parts = document.cookie.split(";")
    for (const part of parts) {
      const [k, ...rest] = part.trim().split("=")
      if (k === ATTRIBUTION_COOKIE_NAME) return rest.join("=")
    }
  } catch {
    return undefined
  }
  return undefined
}

function bindInvalidationListeners(): void {
  if (listenersBound || typeof window === "undefined") return
  listenersBound = true
  window.addEventListener(CONSENT_CHANGED_EVENT, invalidateClientContextCache)
}

/**
 * Buckets screen width for reporting without high-cardinality raw resolutions.
 *
 * @param {number} width - Screen width in CSS pixels.
 * @returns {string} Coarse viewport bucket label.
 */
export function viewportBucketForWidth(width: number): string {
  if (width <= 0 || !Number.isFinite(width)) return "unknown"
  if (width < 360) return "narrow_mobile"
  if (width < 430) return "mobile"
  if (width < 768) return "large_mobile"
  if (width < 1024) return "tablet"
  if (width < 1440) return "laptop"
  if (width < 1920) return "desktop"
  return "wide_desktop"
}

/**
 * Returns cached client context for the current page load (computed once).
 *
 * @returns {ClientContextProps} Diagnostic properties safe for GA4 custom dimensions.
 */
export function getClientContextProps(): ClientContextProps {
  bindInvalidationListeners()
  if (cached) return cached

  if (typeof window === "undefined") {
    cached = {
      viewport_bucket: "unknown",
      in_app_browser: false,
      in_app_app: "none",
      region_consent_required: false,
      analytics_consent_state: "unknown",
      storage_attribution_present: false,
    }
    return cached
  }

  const inApp = detectInAppBrowser()
  let storagePresent = false
  try {
    storagePresent = Boolean(localStorage.getItem(ATTRIBUTION_STORAGE_KEY))
  } catch {
    storagePresent = false
  }
  if (!storagePresent) {
    storagePresent = parseAttributionCookie(readAttributionCookieRaw()) !== null
  }

  cached = {
    viewport_bucket: viewportBucketForWidth(window.screen.width),
    in_app_browser: inApp.isInApp,
    in_app_app: inApp.app ?? "none",
    region_consent_required: isConsentRequiredRegionClient(),
    analytics_consent_state: analyticsConsentState(),
    storage_attribution_present: storagePresent,
  }
  return cached
}
